import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import { fetchPosts } from '../../store/slices/post.slice'
import { ArticleTag } from './ArticleTag'
import { useMediaQuery } from 'react-responsive'
import { useAuthorMap } from '../../hooks/useAuthorMap'
import { PostAuthorLine } from '../PostSystem/PostAuthorLine'
import { Loader } from '../FarmerMotion/Loader'
import { Button, GreyBorderBottom } from '../ui/elements'

export function Articles() {
  const dispatch = useDispatch()
  const [posts, setPosts] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  const authorMap = useAuthorMap()
  const isMobile = useMediaQuery({ maxWidth: 767 })
  const [maxPosts, setMaxPosts] = useState(isMobile ? 8 : 12)

  useEffect(() => {
    setMaxPosts(isMobile ? 8 : 12)
  }, [isMobile])

  useEffect(() => {
    dispatch(fetchPosts({ limit: 16 }))
      .unwrap()
      .then(setPosts)
      .catch((err) => console.error('❌ שגיאה בטעינת פוסטים:', err))
      .finally(() => setIsLoading(false))
  }, [dispatch])

  const postsArray = useMemo(() => (Array.isArray(posts) ? posts : []), [posts])

  const newPosts = useMemo(
    () =>
      [...postsArray]
        .filter((post) => post?.title)
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
        .slice(0, maxPosts),
    [postsArray, maxPosts]
  )

  const topLikedPosts = useMemo(
    () =>
      [...postsArray]
        .filter((post) => Array.isArray(post.likes))
        .sort((a, b) => (b.likes?.length || 0) - (a.likes?.length || 0))
        .slice(0, 2),
    [postsArray]
  )

  if (isLoading) return <Loader />

  return (
    <section className="mt-6 px-2">
      <div className="flex justify-between items-center">
        <h2 className="text-h2 text-softText">מעניין לקרוא</h2>
        <div className="relative">
          <Button
            to="/posts"
            text="לכל המאמרים"
            width={150}
            height={60}
            fontSize="text-lg"
            textColor="text-buttonSecondary1"
            bgColor="fill-accentLight"
            hoverBgColor="hover:scale-105 transform duration-300"
            aria-label="מעבר לעמוד כל האנימות באניזון"
            title="צפה ברשימת כל האנימות באניזון"
          />
        </div>
      </div>

      <div className="md:grid md:grid-cols-[30%_70%] gap-8 ">
        {/* topArticels */}
        <div className="mt-2">
          <div>
            <h5>המובילות</h5>
            <GreyBorderBottom className="w-full" />
          </div>
          <div className="grid grid-cols-1 gap-4">
            {topLikedPosts.map((post) => {
              return (
                <article
                  key={post._id}
                  className="mt-5 relative"
                  aria-labelledby={`top-post-title-${post._id}`}
                >
                  {post.imageUrl && (
                    <img
                      src={post.imageUrl}
                      alt={`תמונה מתוך המאמר: ${post.title}`}
                      title={`תמונה מתוך המאמר: ${post.title}`}
                      className="w-full h-[220px] object-cover mb-4"
                      loading="lazy"
                    />
                  )}
                  <div className="absolute -translate-y-[90%] right-4">
                    <ArticleTag tags={post.tags} />
                  </div>
                  <h3
                    id={`top-post-title-${post._id}`}
                    className="text-xl text-white mt-8 max-w-[320px]"
                  >
                    <Link
                      to={`/post/${post._id}`}
                      title={`פתח את המאמר: ${post.title}`}
                      aria-label={`מעבר למאמר: ${post.title}`}
                    >
                      {post.title}
                    </Link>
                  </h3>
                  <PostAuthorLine
                    userId={post.author?.userId}
                    createdAt={post.createdAt}
                  />
                </article>
              )
            })}
          </div>
        </div>

        {/* newArticels */}
        <div className="mt-2">
          <div>
            <h5>חדש באתר</h5>
            <GreyBorderBottom className="w-full" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-2">
            {newPosts.map((post) => {
              return (
                <article
                  key={post._id}
                  className="grid grid-cols-[92px_1fr] lg:grid-cols-[155px_1fr] gap-4"
                  aria-labelledby={`new-post-title-${post._id}`}
                >
                  <Link
                    to={`/post/${post._id}`}
                    title={`קרא את המאמר: ${post.title}`}
                    aria-label={`מעבר למאמר: ${post.title}`}
                  >
                    {post.imageUrl && (
                      <img
                        src={post.imageUrl}
                        alt={`תמונה מתוך המאמר: ${post.title}`}
                        title={`תמונה מתוך המאמר: ${post.title}`}
                        className="w-[92px] h-[76px] lg:w-[155px] lg:h-[122px] object-cover"
                        loading="lazy"
                      />
                    )}
                  </Link>
                  <div className="flex flex-col justify-center">
                    <h3
                      id={`new-post-title-${post._id}`}
                      className="text-xl text-white"
                    >
                      <Link
                        to={`/post/${post._id}`}
                        title={`קרא את המאמר: ${post.title}`}
                        aria-label={`מאמר חדש: ${post.title}`}
                      >
                        {post.title}
                      </Link>
                    </h3>
                    <PostAuthorLine
                      userId={post.author?.userId}
                      createdAt={post.createdAt}
                    />
                  </div>
                </article>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}
